import { useSupplementalFields } from './SupplementalFields'
import type { FieldValueResult } from '@/components/documents/Foxit/FormMapping/composables/FieldValueProvider'
import { useFormFillHelper } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import type { MappedCartItem } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'

export const useCashAdvanceFields = () => {
	const {
		findCartItems,
		getFieldPrice,
		getFieldDescription,
		getAttributeValue,
		multilineOtherFields,
		generateOtherLineFieldNames,
		findCartItemsByCategories,
		otherLineCounts,
	} = useFormFillHelper()

	const { getSupplementalFieldValues } = useSupplementalFields()

	const deathCertificatesDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.CashAdvance,
				PbSubcategory.CashAdvanceDeathCertificates
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'cashAdvance',
			},
		]
	}

	const deathCertificatesPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.CashAdvance,
				PbSubcategory.CashAdvanceDeathCertificates
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'cashAdvance',
				supplementalItems: items,
			},
		]
	}

	// This is used to get the amount per death certificate
	const deathCertificatesPricePer = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.CashAdvance,
				PbSubcategory.CashAdvanceDeathCertificates
			)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'amountPerDeathCertificate'),
				format: false,
				// unique section to avoid considering during totals
				section: 'cashAdvance-value',
			},
		]
	}

	const deathCertificatesFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.CashAdvance,
			PbSubcategory.CashAdvanceDeathCertificates
		)

		return [
			...deathCertificatesDesc(fieldNames.description, items),
			...deathCertificatesPrice(fieldNames.price, items),
		]
	}

	const musicDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.CashAdvance, PbSubcategory.CashAdvanceMusic)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'cashAdvance',
			},
		]
	}

	const musicPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.CashAdvance, PbSubcategory.CashAdvanceMusic)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'cashAdvance',
				supplementalItems: items,
			},
		]
	}

	const musicFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.CashAdvance,
			PbSubcategory.CashAdvanceMusic
		)

		return [
			...musicDesc(fieldNames.description, items),
			...musicPrice(fieldNames.price, items),
		]
	}

	const coronerMedExaminerDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.CashAdvance,
				PbSubcategory.CashAdvanceCoronerMedExaminer
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'cashAdvance',
			},
		]
	}

	const coronerMedExaminerPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.CashAdvance,
				PbSubcategory.CashAdvanceCoronerMedExaminer
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'cashAdvance',
				supplementalItems: items,
			},
		]
	}

	const coronerMedExaminerFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.CashAdvance,
			PbSubcategory.CashAdvanceCoronerMedExaminer
		)

		return [
			...coronerMedExaminerDesc(fieldNames.description, items),
			...coronerMedExaminerPrice(fieldNames.price, items),
		]
	}

	const flowersDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.CashAdvance, PbSubcategory.CashAdvanceFlowers)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'cashAdvance',
			},
		]
	}

	const flowersPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.CashAdvance, PbSubcategory.CashAdvanceFlowers)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'cashAdvance',
				supplementalItems: items,
			},
		]
	}

	const flowersFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.CashAdvance,
			PbSubcategory.CashAdvanceFlowers
		)

		return [
			...flowersDesc(fieldNames.description, items),
			...flowersPrice(fieldNames.price, items),
		]
	}

	const obituaryNoticePublicationDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.CashAdvance,
				PbSubcategory.CashAdvanceObituary
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'cashAdvance',
			},
		]
	}

	const obituaryNoticePublicationPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.CashAdvance,
				PbSubcategory.CashAdvanceObituary
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'cashAdvance',
				supplementalItems: items,
			},
		]
	}

	const obituaryNoticePublicationFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.CashAdvance,
			PbSubcategory.CashAdvanceObituary
		)

		return [
			...obituaryNoticePublicationDesc(fieldNames.description, items),
			...obituaryNoticePublicationPrice(fieldNames.price, items),
		]
	}

	const clergyHonorariumDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.CashAdvance,
				PbSubcategory.CashAdvanceClergyHonorarium
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'cashAdvance',
			},
		]
	}

	const clergyHonorariumPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.CashAdvance,
				PbSubcategory.CashAdvanceClergyHonorarium
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'cashAdvance',
				supplementalItems: items,
			},
		]
	}

	const clergyHonorariumFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.CashAdvance,
			PbSubcategory.CashAdvanceClergyHonorarium
		)

		return [
			...clergyHonorariumDesc(fieldNames.description, items),
			...clergyHonorariumPrice(fieldNames.price, items),
		]
	}

	const nonGuaranteedCremationServicesPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.CashAdvance,
				PbSubcategory.CashAdvanceCremationServices
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'cashAdvance',
				supplementalItems: items,
			},
		]
	}

	const salesTaxPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Other, PbSubcategory.OtherSalesTax)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'cashAdvanceTax',
				supplementalItems: items,
			},
		]
	}

	const multilineCashAdvanceOtherFields = (
		fieldNames: string
	): FieldValueResult[] => {
		if (!otherLineCounts.cashAdvance) {
			console.error('CashAdvance other line count not set')

			return []
		}

		const items = findCartItems(PbProductType.CashAdvance, PbSubcategory.Other)
		const fields = generateOtherLineFieldNames(
			fieldNames,
			otherLineCounts.cashAdvance
		)

		return multilineOtherFields(fields, items, 'cashAdvance')
	}

	const multilineCashAdvanceOtherFieldsWithDiscount = (
		fieldNames: string
	): FieldValueResult[] => {
		if (!otherLineCounts.cashAdvance) {
			console.error('CashAdvance other line count not set')

			return []
		}

		const items = [
			...findCartItems(PbProductType.CashAdvance, PbSubcategory.Discount),
			...findCartItems(PbProductType.CashAdvance, PbSubcategory.Other),
		]

		const fields = generateOtherLineFieldNames(
			fieldNames,
			otherLineCounts.cashAdvance
		)

		return multilineOtherFields(fields, items, 'cashAdvance')
	}

	//**
	// Special Scenario: This function should only be used when the form has missing cash advance fields.
	// This will not show on the form, but since the calculations for the funeral total uses the form fill array,
	// we need to include it here to ensure the totals are correct.
	// */
	const allCashAdvanceFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItemsByCategories([
			PbProductType.CashAdvance,
			PbProductType.Cemetery,
			PbProductType.FamilyTravel,
			PbProductType.Afterward,
			PbProductType.Other,
		])
		if (!items) {
			return []
		}

		return [
			{
				name: fieldNames.price,
				value: getFieldPrice(items),
				format: false,
				section: 'cashAdvance',
			},
		]
	}

	//**
	// Special Scenario: This function should only be used when the form has missing cash advance fields.
	// This is used to get all the cash advance items, groups them by subcategory and prepares them for the supplemental page.
	// */
	const allCashAdvanceItemsOnSupplemental = (): FieldValueResult[] => {
		const items = findCartItemsByCategories([
			PbProductType.CashAdvance,
			PbProductType.Cemetery,
			PbProductType.FamilyTravel,
			PbProductType.Afterward,
			PbProductType.Other,
		])
		const section = 'supplementalCashAdvance'

		const itemsBySubcategory = items.reduce<Map<string, MappedCartItem[]>>(
			(grouped, item) => {
				const subcategoryItems = grouped.get(item.subcategory) ?? []
				grouped.set(item.subcategory, [...subcategoryItems, item])
				return grouped
			},
			new Map()
		)

		const subcategoryMaps = [...itemsBySubcategory.entries()].map(
			([subcategory, subcategoryItems]) =>
				new Map<string, MappedCartItem[]>().set(subcategory, subcategoryItems)
		)

		const supplementalPageItems = new Map<
			string,
			Map<string, MappedCartItem[]>[]
		>([[section, subcategoryMaps]])

		return getSupplementalFieldValues(supplementalPageItems)
	}

	return {
		deathCertificatesDesc,
		deathCertificatesPrice,
		deathCertificatesPricePer,
		deathCertificatesFields,
		multilineCashAdvanceOtherFields,
		multilineCashAdvanceOtherFieldsWithDiscount,
		musicDesc,
		musicPrice,
		musicFields,
		coronerMedExaminerDesc,
		coronerMedExaminerPrice,
		coronerMedExaminerFields,
		flowersDesc,
		flowersPrice,
		flowersFields,
		obituaryNoticePublicationDesc,
		obituaryNoticePublicationPrice,
		obituaryNoticePublicationFields,
		clergyHonorariumDesc,
		clergyHonorariumPrice,
		clergyHonorariumFields,
		nonGuaranteedCremationServicesPrice,
		salesTaxPrice,
		allCashAdvanceFields,
		allCashAdvanceItemsOnSupplemental,
	}
}
